// src/adminCalendar/workerPermissions.js
// LOOP WORKER PERMISSIONS SAVE HOTFIX — manual permission overrides are preserved.
// LOOP BUSINESS OS — Phase 2 permissions + business settings foundation.
// Centralized role defaults, permission groups and helpers.

export const LOOP_BUSINESS_ROLES = [
  { key: "owner", label: "Dueño", description: "Control total del negocio." },
  { key: "admin", label: "Admin", description: "Opera y configura casi todo el negocio." },
  { key: "reception", label: "Recepción", description: "Agenda, clientes, caja y comunicación diaria." },
  { key: "worker", label: "Trabajador", description: "Acceso enfocado en su propia agenda." },
  { key: "finance", label: "Finanzas", description: "Reportes, pagos, comisiones e inventario financiero." },
  { key: "readonly", label: "Solo lectura", description: "Visibilidad sin permisos de edición." },
];

export const DEFAULT_WORKER_PERMISSIONS = {
  // Agenda
  canViewOwnCalendar: true,
  canViewAllCalendars: false,
  canViewSelectedCalendars: false,
  canViewPrices: false,
  canViewCustomerPhone: true,
  canViewCustomerEmail: false,
  canCreateBookings: false,
  canEditBookings: false,
  canCompleteBookings: false,
  canAcceptBookings: true,
  canRejectBookings: true,
  canCancelBookings: false,
  canMoveBookings: false,
  canOverrideBookingConflicts: false,
  canBookOutsideWorkingHours: false,
  canManageOwnAvailability: false,
  canViewAnalytics: false,

  // CRM / clientes
  canViewCustomers: false,
  canEditCustomers: false,

  // Finanzas
  canViewReports: false,
  canViewFinancials: false,
  canViewPaymentMethods: false,
  canViewCommissions: false,
  canEditPayments: false,
  canEditCommissions: false,

  // Compensación propia del trabajador
  canViewOwnTips: false,
  canViewOwnPayout: false,
  canViewOwnCommissionBreakdown: false,

  // Inventario
  canViewInventory: false,
  canEditInventory: false,
  canAdjustInventory: false,
  canViewInventoryCosts: false,

  // Comunicación
  canSendWhatsApp: false,

  // Auditoría
  canViewAuditLogs: false,

  // Administración
  canManageWorkers: false,
  canManageBusinessSettings: false,
  canManageModules: false,
  canManageBilling: false,
};

export const ROLE_PERMISSION_PRESETS = {
  owner: Object.fromEntries(Object.keys(DEFAULT_WORKER_PERMISSIONS).map((key) => [key, true])),

  admin: {
    ...DEFAULT_WORKER_PERMISSIONS,
    canViewOwnCalendar: true,
    canViewAllCalendars: true,
    canViewPrices: true,
    canViewCustomerPhone: true,
    canViewCustomerEmail: true,
    canCreateBookings: true,
    canEditBookings: true,
    canCompleteBookings: true,
    canAcceptBookings: true,
    canRejectBookings: true,
    canCancelBookings: true,
    canMoveBookings: true,
    canManageOwnAvailability: true,
    canViewAnalytics: true,
    canViewCustomers: true,
    canEditCustomers: true,
    canViewReports: true,
    canViewFinancials: true,
    canViewPaymentMethods: true,
    canViewCommissions: true,
    canEditPayments: true,
    canEditCommissions: true,
    canViewInventory: true,
    canEditInventory: true,
    canAdjustInventory: true,
    canViewInventoryCosts: true,
    canSendWhatsApp: true,
    canViewAuditLogs: true,
    canManageWorkers: true,
    canManageBusinessSettings: true,
    canManageModules: true,
    canManageBilling: false,
  },

  reception: {
    ...DEFAULT_WORKER_PERMISSIONS,
    canViewOwnCalendar: true,
    canViewAllCalendars: true,
    canViewPrices: true,
    canViewCustomerPhone: true,
    canViewCustomerEmail: true,
    canCreateBookings: true,
    canEditBookings: true,
    canCompleteBookings: true,
    canAcceptBookings: true,
    canRejectBookings: true,
    canCancelBookings: true,
    canMoveBookings: true,
    canViewCustomers: true,
    canEditCustomers: true,
    canViewReports: true,
    canViewFinancials: true,
    canViewPaymentMethods: true,
    canViewCommissions: true,
    canEditPayments: true,
    canEditCommissions: false,
    canViewInventory: true,
    canEditInventory: false,
    canAdjustInventory: false,
    canViewInventoryCosts: false,
    canSendWhatsApp: true,
    canViewAuditLogs: false,
  },

  worker: {
    ...DEFAULT_WORKER_PERMISSIONS,
  },

  finance: {
    ...DEFAULT_WORKER_PERMISSIONS,
    canViewOwnCalendar: false,
    canViewAllCalendars: false,
    canViewPrices: true,
    canViewCustomerPhone: false,
    canViewCustomerEmail: false,
    canViewAnalytics: true,
    canViewReports: true,
    canViewFinancials: true,
    canViewPaymentMethods: true,
    canViewCommissions: true,
    canEditPayments: false,
    canEditCommissions: false,
    canViewInventory: true,
    canEditInventory: false,
    canAdjustInventory: false,
    canViewInventoryCosts: true,
    canViewCustomers: false,
    canEditCustomers: false,
    canViewAuditLogs: false,
  },

  readonly: {
    ...DEFAULT_WORKER_PERMISSIONS,
    canViewOwnCalendar: false,
    canViewAllCalendars: true,
    canViewPrices: true,
    canViewCustomerPhone: false,
    canViewCustomerEmail: false,
    canViewAnalytics: true,
    canViewReports: true,
    canViewFinancials: false,
    canViewPaymentMethods: true,
    canViewCustomers: true,
    canViewInventory: true,
  },
};

export const DEFAULT_LOOP_BUSINESS_SETTINGS = {
  loopPlan: "early_access",
  trialStatus: "active",
  enabledModules: {
    analytics: true,
    inventory: true,
    crm: true,
    auditLogs: true,
    whatsappManual: true,
    whatsappAuto: false,
    payments: false,
    loyalty: false,
    memberships: false,
    calendarSync: false,
  },
  financeSettings: {
    showPaymentMethods: true,
    showWorkerPayouts: true,
    allowManualPayoutOverride: true,
    receptionFinanceVisibility: "full", // full | payment_only
    financeCanEditPaymentsDefault: false,
  },
  inventorySettings: {
    enabled: true,
    plan: "early_access", // early_access | addon | disabled
    autoDeduct: true,
    deductTrigger: "completed", // completed | paid
    allowNegativeStock: false,
  },
  workerSettings: {
    compensationVisibilityDefault: "hidden", // hidden | tips_only | payout_total | full_breakdown
  },
  auditSettings: {
    enabled: true,
    defaultVisibility: ["owner", "admin"],
  },
  paymentSettings: {
    depositsEnabled: false,
    provider: "manual", // manual | pagalo_facil | wompi | paypal | stripe
    depositType: "fixed",
    depositValue: 0,
    refundPolicy: "manual",
  },
};

export const WORKER_PERMISSION_GROUPS = [
  {title:"Excepciones de horario",description:"Solo el dueño puede conceder estos permisos. Cada excepción requiere un motivo y queda registrada.",items:[["canOverrideBookingConflicts","Autorizar reservas con conflicto de horario"],["canBookOutsideWorkingHours","Autorizar reservas fuera del horario laboral"]]},
  {
    title: "Agenda",
    description: "Controla qué puede ver esta persona en el calendario.",
    items: [
      ["canViewOwnCalendar", "Ver su propia agenda"],
      ["canViewAllCalendars", "Ver agenda de todos"],
      ["canViewPrices", "Ver precios y totales"],
      ["canViewCustomerPhone", "Ver teléfono del cliente"],
      ["canViewCustomerEmail", "Ver correo del cliente"],
      ["canManageOwnAvailability", "Editar su horario/disponibilidad"],
    ],
  },
  {
    title: "Reservas",
    description: "Define qué acciones puede ejecutar sobre reservas.",
    items: [
      ["canAcceptBookings", "Aceptar reservas"],
      ["canRejectBookings", "Rechazar reservas"],
      ["canCreateBookings", "Crear reservas manuales"],
      ["canEditBookings", "Editar reservas"],
      ["canCompleteBookings", "Marcar reservas completadas"],
      ["canCancelBookings", "Cancelar reservas"],
      ["canMoveBookings", "Mover reservas en calendario"],
    ],
  },
  {
    title: "Clientes / CRM",
    description: "Acceso a la base de clientes y datos comerciales.",
    items: [
      ["canViewCustomers", "Ver clientes"],
      ["canEditCustomers", "Editar clientes"],
    ],
  },
  {
    title: "Finanzas",
    description: "Reportes, caja, pagos, comisiones y neto del negocio.",
    items: [
      ["canViewAnalytics", "Ver analytics"],
      ["canViewReports", "Ver reportes"],
      ["canViewFinancials", "Ver finanzas"],
      ["canViewPaymentMethods", "Ver métodos de pago"],
      ["canViewCommissions", "Ver comisiones"],
      ["canEditPayments", "Editar pagos / marcar cobrado"],
      ["canEditCommissions", "Editar comisiones"],
    ],
  },
  {
    title: "Compensación propia",
    description: "Qué puede ver un trabajador sobre su propio payout.",
    items: [
      ["canViewOwnTips", "Ver sus propinas"],
      ["canViewOwnPayout", "Ver su pago total"],
      ["canViewOwnCommissionBreakdown", "Ver desglose de comisión/propina"],
    ],
  },
  {
    title: "Inventario",
    description: "Productos, stock, costos y ajustes.",
    items: [
      ["canViewInventory", "Ver inventario"],
      ["canEditInventory", "Editar inventario"],
      ["canAdjustInventory", "Ajustar stock"],
      ["canViewInventoryCosts", "Ver costos de inventario"],
    ],
  },
  {
    title: "Comunicación y auditoría",
    description: "Mensajes, historial de acciones y control interno.",
    items: [
      ["canSendWhatsApp", "Enviar WhatsApp"],
      ["canViewAuditLogs", "Ver historial/auditoría"],
    ],
  },
  {
    title: "Administración",
    description: "Configuraciones sensibles del negocio.",
    items: [
      ["canManageWorkers", "Gestionar equipo"],
      ["canManageBusinessSettings", "Gestionar ajustes del negocio"],
      ["canManageModules", "Gestionar módulos"],
      ["canManageBilling", "Gestionar plan/facturación"],
    ],
  },
];

export function normalizeBusinessRole(value) {
  const raw = String(value || "worker").trim().toLowerCase();
  if (["owner", "dueno", "dueño", "propietario"].includes(raw)) return "owner";
  if (["admin", "administrator", "manager", "gerente"].includes(raw)) return "admin";
  if (["reception", "recepcion", "recepción", "frontdesk", "front_desk"].includes(raw)) return "reception";
  if (["finance", "finanzas", "finance_role"].includes(raw)) return "finance";
  if (["readonly", "read_only", "solo_lectura", "viewer"].includes(raw)) return "readonly";
  if (["worker", "employee", "trabajador", "colaborador"].includes(raw)) return "worker";
  return "worker";
}

export function getRolePermissionPreset(role) {
  const key = normalizeBusinessRole(role);
  return { ...(ROLE_PERMISSION_PRESETS[key] || ROLE_PERMISSION_PRESETS.worker) };
}

export function getAccessRole(source = {}) {
  return normalizeBusinessRole(source.accessRole || source.loopRole || source.securityRole || source.businessRole || "worker");
}

// LOOP WORKER PERMISSIONS SAVE HOTFIX — detect explicit manual permissions.
// Important: applyBusinessSettingsToPermissions can receive either an employee doc,
// a { permissions } object, or an already-normalized permission map. In every case,
// boolean keys are treated as an intentional manual value and should not be replaced
// by role/business defaults except when a whole module is disabled.
function extractExplicitWorkerPermissions(source = {}) {
  if (!source || typeof source !== "object") return {};
  const raw = source.permissions && typeof source.permissions === "object"
    ? source.permissions
    : source.workerPermissions && typeof source.workerPermissions === "object"
      ? source.workerPermissions
      : source;
  const out = {};
  Object.keys(DEFAULT_WORKER_PERMISSIONS).forEach((key) => {
    if (typeof raw?.[key] === "boolean") out[key] = raw[key];
  });
  return out;
}


export function normalizeWorkerPermissions(source) {
  const raw = source?.permissions || source?.workerPermissions || source || {};
  const role = getAccessRole(source || {});
  const base = source?.accessRole || source?.loopRole || source?.securityRole || source?.businessRole
    ? getRolePermissionPreset(role)
    : { ...DEFAULT_WORKER_PERMISSIONS };
  const merged = { ...base };

  Object.keys(DEFAULT_WORKER_PERMISSIONS).forEach((key) => {
    if (typeof raw?.[key] === "boolean") merged[key] = raw[key];
  });

  // Permission dependencies / safety rails.
  merged.allowedCalendarIds = Array.isArray(raw.allowedCalendarIds) ? [...new Set(raw.allowedCalendarIds.filter(id => typeof id === 'string' && id))] : [];
  if (merged.canViewAllCalendars) merged.canViewOwnCalendar = true;
  if (merged.canEditBookings || merged.canCompleteBookings || merged.canMoveBookings || merged.canCancelBookings || merged.canCreateBookings) {
    merged.canViewOwnCalendar = true;
  }
  if (merged.canEditCustomers) merged.canViewCustomers = true;
  if (merged.canEditPayments || merged.canEditCommissions) merged.canViewFinancials = true;
  if (merged.canViewFinancials) {
    merged.canViewReports = true;
    merged.canViewAnalytics = true;
    merged.canViewPaymentMethods = true;
  }
  if (merged.canViewCommissions) merged.canViewFinancials = true;
  if (merged.canEditInventory || merged.canAdjustInventory || merged.canViewInventoryCosts) merged.canViewInventory = true;
  if (merged.canViewOwnCommissionBreakdown) {
    merged.canViewOwnPayout = true;
    merged.canViewOwnTips = true;
  }
  if (merged.canViewOwnPayout) merged.canViewOwnTips = true;
  if (merged.canManageWorkers || merged.canManageBusinessSettings || merged.canManageModules || merged.canManageBilling) {
    merged.canViewReports = true;
  }

  return merged;
}

export function normalizeLoopBusinessSettings(source = {}) {
  const data = source && typeof source === "object" ? source : {};
  return {
    loopPlan: data.loopPlan || DEFAULT_LOOP_BUSINESS_SETTINGS.loopPlan,
    trialStatus: data.trialStatus || DEFAULT_LOOP_BUSINESS_SETTINGS.trialStatus,
    enabledModules: { ...DEFAULT_LOOP_BUSINESS_SETTINGS.enabledModules, ...(data.enabledModules || {}) },
    financeSettings: { ...DEFAULT_LOOP_BUSINESS_SETTINGS.financeSettings, ...(data.financeSettings || {}) },
    inventorySettings: { ...DEFAULT_LOOP_BUSINESS_SETTINGS.inventorySettings, ...(data.inventorySettings || {}) },
    workerSettings: { ...DEFAULT_LOOP_BUSINESS_SETTINGS.workerSettings, ...(data.workerSettings || {}) },
    auditSettings: { ...DEFAULT_LOOP_BUSINESS_SETTINGS.auditSettings, ...(data.auditSettings || {}) },
    paymentSettings: { ...DEFAULT_LOOP_BUSINESS_SETTINGS.paymentSettings, ...(data.paymentSettings || {}) },
  };
}

// LOOP PHASE 2.0.4 — apply business-level settings/modules on top of role permissions.
export function applyBusinessSettingsToPermissions(permissions, businessSettingsSource = {}, roleSource = {}) {
  const settings = normalizeLoopBusinessSettings(businessSettingsSource);
  const role = normalizeBusinessRole(
    roleSource?.accessRole ||
    roleSource?.loopRole ||
    roleSource?.securityRole ||
    roleSource?.businessRole ||
    roleSource?.role ||
    "worker"
  );
  const perms = normalizeWorkerPermissions(permissions);
  const explicitPermissions = extractExplicitWorkerPermissions(permissions);
  const hasExplicitPermission = (key) => typeof explicitPermissions?.[key] === "boolean";

  const modules = settings.enabledModules || {};
  const finance = settings.financeSettings || {};
  const inventory = settings.inventorySettings || {};
  const worker = settings.workerSettings || {};
  const audit = settings.auditSettings || {};
  const payments = settings.paymentSettings || {};

  // Module switches should disable related capabilities even if a preset has them.
  if (modules.analytics === false) {
    perms.canViewAnalytics = false;
    perms.canViewReports = false;
  }

  if (modules.crm === false) {
    perms.canViewCustomers = false;
    perms.canEditCustomers = false;
  }

  if (modules.whatsappManual === false) {
    perms.canSendWhatsApp = false;
  }

  if (modules.auditLogs === false || audit.enabled === false) {
    perms.canViewAuditLogs = false;
  }

  const inventoryEnabled = modules.inventory !== false && inventory.enabled !== false && inventory.plan !== "disabled";
  if (!inventoryEnabled) {
    perms.canViewInventory = false;
    perms.canEditInventory = false;
    perms.canAdjustInventory = false;
    perms.canViewInventoryCosts = false;
  }

  const onlinePaymentsEnabled = modules.payments === true && payments.depositsEnabled === true;
  if (!onlinePaymentsEnabled) {
    perms.canManageBilling = false;
  }

  // Reception defaults to full finance visibility in Loop, but the owner can downgrade it.
  if (role === "reception" && finance.receptionFinanceVisibility === "payment_only") {
    perms.canViewFinancials = false;
    perms.canViewCommissions = false;
    perms.canEditCommissions = false;
    perms.canViewInventoryCosts = false;
    perms.canViewReports = Boolean(perms.canViewAnalytics);
    perms.canViewPaymentMethods = true;
  }

  // Finance can see everything financial, but editing remains controlled by this setting/explicit override.
  if (role === "finance" && finance.financeCanEditPaymentsDefault !== true && !permissions?.canEditPayments) {
    perms.canEditPayments = false;
    perms.canEditCommissions = false;
  }

  if (finance.showPaymentMethods === false) perms.canViewPaymentMethods = false;
  if (finance.showWorkerPayouts === false) {
    perms.canViewCommissions = false;
    perms.canEditCommissions = false;
  }
  if (finance.allowManualPayoutOverride === false) perms.canEditCommissions = false;

  // Worker compensation visibility is controlled by business default only when there is no
  // explicit manual permission override. Previously this always overwrote saved switches
  // for worker compensation, making it look like permissions did not save.
  const visibility = String(worker.compensationVisibilityDefault || "hidden").toLowerCase();
  const compensationKeys = ["canViewOwnTips", "canViewOwnPayout", "canViewOwnCommissionBreakdown"];
  const compensationOverride = String(roleSource?.compensationVisibilityOverride || "").trim().toLowerCase();
  const hasManualCompensationPermissions = compensationKeys.some((key) => hasExplicitPermission(key));
  const shouldApplyBusinessCompensationDefault =
    role === "worker" &&
    !hasManualCompensationPermissions &&
    (!compensationOverride || compensationOverride === "business_default");

  if (shouldApplyBusinessCompensationDefault) {
    if (visibility === "hidden") {
      perms.canViewOwnTips = false;
      perms.canViewOwnPayout = false;
      perms.canViewOwnCommissionBreakdown = false;
    } else if (visibility === "tips_only") {
      perms.canViewOwnTips = true;
      perms.canViewOwnPayout = false;
      perms.canViewOwnCommissionBreakdown = false;
    } else if (visibility === "payout_total") {
      perms.canViewOwnTips = true;
      perms.canViewOwnPayout = true;
      perms.canViewOwnCommissionBreakdown = false;
    } else if (visibility === "full_breakdown") {
      perms.canViewOwnTips = true;
      perms.canViewOwnPayout = true;
      perms.canViewOwnCommissionBreakdown = true;
    }
  }

  return perms;
}

export function isLoopModuleEnabled(businessSettingsSource = {}, moduleKey) {
  const settings = normalizeLoopBusinessSettings(businessSettingsSource);
  return settings.enabledModules?.[moduleKey] !== false;
}

export function hasPermission(permissions, key) {
  const perms = normalizeWorkerPermissions(permissions);
  return Boolean(perms && perms[key]);
}

export function getWorkerCompensationVisibility(employee = {}, businessDoc = {}) {
  const override = String(employee.compensationVisibilityOverride || "business_default").trim().toLowerCase();
  if (["hidden", "tips_only", "payout_total", "full_breakdown"].includes(override)) return override;

  const settings = normalizeLoopBusinessSettings(businessDoc);
  const fromBusiness = String(settings.workerSettings?.compensationVisibilityDefault || "hidden").trim().toLowerCase();
  if (["hidden", "tips_only", "payout_total", "full_breakdown"].includes(fromBusiness)) return fromBusiness;
  return "hidden";
}

export function canActorManageBusiness(businessDoc, actor) {
  if (!businessDoc || !actor) return false;
  const uid = actor.uid ? String(actor.uid) : null;
  const email = actor.email ? String(actor.email).trim().toLowerCase() : "";
  if (businessDoc.ownerUid && uid && String(businessDoc.ownerUid) === uid) return true;
  if (businessDoc.ownerEmail && email && String(businessDoc.ownerEmail).trim().toLowerCase() === email) return true;
  if (businessDoc.admins && uid && uid in businessDoc.admins) return true;
  if (businessDoc.managers && uid && uid in businessDoc.managers) return true;
  return false;
}

export function isBookingAssignedToWorker(bookingLike, actor, employeeDocId) {
  const booking = bookingLike?.extendedProps?.raw || bookingLike?.raw || bookingLike?.booking || bookingLike || {};
  const actorEmail = String(actor?.email || "").trim().toLowerCase();
  const actorUid = actor?.uid ? String(actor.uid) : "";
  const detectedId = employeeDocId ? String(employeeDocId) : "";

  const directIds = [booking.employeeId, booking.resourceId, booking.workerId, booking.assignedEmployeeId]
    .filter(Boolean)
    .map(String);
  if (detectedId && directIds.includes(detectedId)) return true;
  if (actorUid && directIds.includes(actorUid)) return true;

  const directEmails = [booking.employeeEmail, booking.workerEmail, booking.assignedEmployeeEmail]
    .filter(Boolean)
    .map((v) => String(v).trim().toLowerCase());
  if (actorEmail && directEmails.includes(actorEmail)) return true;

  const assignedIds = Array.isArray(booking.assignedEmployeeIds) ? booking.assignedEmployeeIds.map(String) : [];
  if (detectedId && assignedIds.includes(detectedId)) return true;
  if (actorUid && assignedIds.includes(actorUid)) return true;

  const assignedEmployees = Array.isArray(booking.assignedEmployees) ? booking.assignedEmployees : [];
  return assignedEmployees.some((emp) => {
    const id = String(emp?.id || emp?.employeeId || emp?.uid || "");
    const email = String(emp?.email || emp?.employeeEmail || emp?.workerEmail || "").trim().toLowerCase();
    return Boolean(
      (detectedId && id && id === detectedId) ||
      (actorUid && id && id === actorUid) ||
      (actorEmail && email && email === actorEmail)
    );
  });
}

export function canWorkerViewBooking(bookingLike, actor, employeeDocId, permissions) {
  const perms = normalizeWorkerPermissions(permissions);
  if (perms.canViewAllCalendars) return true;
  if (perms.canViewSelectedCalendars) {
    const ids = [bookingLike.employeeId, bookingLike.workerId, bookingLike.resourceId, bookingLike.assignedEmployeeId, ...(bookingLike.assignedEmployeeIds || [])].filter(Boolean).map(String);
    if (ids.some(id => perms.allowedCalendarIds.includes(id))) return true;
  }
  if (!perms.canViewOwnCalendar) return false;
  return isBookingAssignedToWorker(bookingLike, actor, employeeDocId);
}
